import { useBotCheck } from '@components/Common/BotCheck';
import FormField from '@components/Common/FormField';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutlineOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Button, Grid, IconButton, InputAdornment } from '@mui/material';
import React, { useState } from 'react';
import './index.scss';

interface AuthenticationProps {
  /** `bot` is the bot check's fields (token, honeypot), sent with the credentials. */
  onSubmit: (email: string, password: string, bot: Record<string, string>) => void;
}

/** 17px leading glyph in --tx-low, per the field spec. */
const leadingIcon = (Icon: typeof MailOutlineIcon) => (
  <InputAdornment position="start">
    <Icon sx={{ fontSize: 17, color: 'var(--tx-low)' }} />
  </InputAdornment>
);

const AuthenticationFormPresentation: React.FC<AuthenticationProps> = ({
  onSubmit,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // True while submit waits for Turnstile's token, which can take a few seconds on a slow phone.
  const [checking, setChecking] = useState(false);
  const bot = useBotCheck('cc_login');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (checking) return;
    setChecking(true);
    try {
      onSubmit(email, password, await bot.fields());
    } finally {
      setChecking(false);
    }
  };

  return (
    <form className='authentication-form-container' onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <FormField
            required
            className="authentication-form-text-field"
            label="Work email"
            name="email"
            type="email"
            value={email}
            onChange={(_, value) => setEmail(value)}
            textFieldProps={{
              autoComplete: 'email',
              slotProps: { input: { startAdornment: leadingIcon(MailOutlineIcon) } },
            }}
          />
        </Grid>
        <Grid size={12}>
          <FormField
            required
            className="authentication-form-text-field"
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(_, value) => setPassword(value)}
            textFieldProps={{
              autoComplete: 'current-password',
              slotProps: {
                input: {
                  startAdornment: leadingIcon(LockOutlinedIcon),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        edge="end"
                        onClick={() => setShowPassword((v) => !v)}
                        size="small"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon sx={{ fontSize: 17, color: 'var(--tx-low)' }} />
                        ) : (
                          <VisibilityOutlinedIcon sx={{ fontSize: 17, color: 'var(--tx-low)' }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
        </Grid>
        {/* The Turnstile mount (hidden while empty) and the honeypot. Direct children of the grid
            so an empty mount adds no gap. */}
        {bot.element}
        <Grid size={12}>
          <Button fullWidth disabled={checking} className="authentication-form-sign-in-button" color="primary" type="submit" variant="contained">
            Sign in
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default AuthenticationFormPresentation;
